// src/pages/Sliders.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const Sliders = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    order: 0,
    buttonText: '',
    buttonLink: '',
    active: true,
    image: null
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    const q = query(collection(db, 'sliders'), orderBy('order', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const sliderData = [];
      snapshot.forEach((doc) => {
        sliderData.push({ id: doc.id, ...doc.data() });
      });
      setSliders(sliderData);
      setLoading(false);
    });

    return () => {
      clearInterval(clockInterval);
      unsubscribe();
    };
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      order: 0,
      buttonText: '',
      buttonLink: '',
      active: true,
      image: null
    });
    setImageFile(null);
    setImagePreview('');
    setEditingSlider(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (slider) => {
    setEditingSlider(slider);
    setFormData({
      title: slider.title || '',
      subtitle: slider.subtitle || '',
      order: slider.order || 0,
      buttonText: slider.buttonText || '',
      buttonLink: slider.buttonLink || '',
      active: slider.active !== false,
      image: slider.image || null
    });
    if (slider.image) {
      setImagePreview(slider.image);
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsUploading(true);

    try {
      let imageUrl = formData.image;

      if (imageFile) {
        const storageRef = ref(storage, `sliders/${Date.now()}_${imageFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, imageFile);
        
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => reject(error),
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              imageUrl = url;
              resolve();
            }
          );
        });
      }

      const sliderData = {
        title: formData.title,
        subtitle: formData.subtitle,
        order: parseInt(formData.order) || 0,
        buttonText: formData.buttonText,
        buttonLink: formData.buttonLink,
        active: formData.active,
        image: imageUrl,
        updatedAt: new Date()
      };

      // If editing and there's an old image, delete it
      if (editingSlider && formData.image && imageFile && formData.image !== imageUrl) {
        try {
          await deleteObject(ref(storage, formData.image));
        } catch (e) {}
      }

      if (editingSlider) {
        await updateDoc(doc(db, 'sliders', editingSlider.id), sliderData);
        alert('Slider updated successfully!');
      } else {
        sliderData.createdAt = new Date();
        await addDoc(collection(db, 'sliders'), sliderData);
        alert('Slider created successfully!');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving slider:', error);
      alert('Error saving slider: ' + error.message);
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this slider?')) return;
    
    setLoading(true);
    try {
      if (imageUrl) {
        try {
          await deleteObject(ref(storage, imageUrl));
        } catch (e) {
          console.warn('Image not found in storage');
        }
      }
      await deleteDoc(doc(db, 'sliders', id));
      alert('Slider deleted successfully!');
    } catch (error) {
      console.error('Error deleting slider:', error);
      alert('Error deleting slider: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const styles = `
    .sliders-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .sliders-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 16px 24px;
      border-radius: 16px;
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      flex-wrap: wrap;
      gap: 16px;
    }

    .sliders-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.25rem;
      font-weight: 700;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .sliders-header h2 i {
      color: #0ea5e9;
    }

    .btn-add-slider {
      padding: 12px 28px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3);
    }

    .btn-add-slider:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 30px rgba(14, 165, 233, 0.4);
    }

    .sliders-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
      gap: 28px;
    }

    .slider-card {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
      display: flex;
      flex-direction: column;
    }

    .slider-card:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.65);
      box-shadow: 0 16px 36px 0 rgba(14, 165, 233, 0.14);
      border-color: rgba(14, 165, 233, 0.3);
    }

    .slider-image-wrapper {
      position: relative;
      overflow: hidden;
      height: 220px;
      background: linear-gradient(135deg, #0f172a, #334155);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
    }

    .slider-actual-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s ease;
    }

    .slider-card:hover .slider-actual-img {
      transform: scale(1.06);
    }

    .slider-image-overlay {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 20px;
      background: linear-gradient(transparent, rgba(0,0,0,0.5));
    }

    .slider-order-badge {
      display: inline-block;
      padding: 4px 12px;
      background: rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(10px);
      border-radius: 20px;
      font-size: 0.7rem;
      font-weight: 700;
      color: white;
    }

    .slider-info {
      padding: 24px;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .slider-title {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.25rem;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 6px;
      line-height: 1.4;
    }

    .slider-subtitle {
      color: #334155;
      font-size: 0.88rem;
      line-height: 1.5;
      margin-bottom: 16px;
    }

    .slider-badge {
      display: inline-block;
      align-self: flex-start;
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 0.72rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 12px;
    }

    .badge-active {
      background: rgba(16, 185, 129, 0.12);
      color: #10b981;
      border: 1px solid rgba(16, 185, 129, 0.2);
    }

    .badge-inactive {
      background: rgba(244, 63, 94, 0.1);
      color: #f43f5e;
      border: 1px solid rgba(244, 63, 94, 0.2);
    }

    .slider-order-label {
      font-size: 0.75rem;
      color: #64748b;
      font-weight: 700;
      text-transform: uppercase;
    }

    .slider-actions {
      display: flex;
      gap: 10px;
      padding-top: 16px;
      border-top: 1px solid rgba(14, 165, 233, 0.15);
      margin-top: auto;
    }

    .btn-row-action {
      flex: 1;
      padding: 10px 14px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.8rem;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: all 0.2s;
      border: none;
    }

    .btn-row-edit {
      background: rgba(14, 165, 233, 0.12);
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: #0ea5e9;
    }

    .btn-row-edit:hover {
      background: #0ea5e9;
      color: white;
      border-color: #0ea5e9;
    }

    .btn-row-delete {
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      color: #f43f5e;
    }

    .btn-row-delete:hover {
      background: #f43f5e;
      color: white;
      border-color: #f43f5e;
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      grid-column: 1/-1;
    }

    .empty-state i {
      font-size: 54px;
      color: #64748b;
      margin-bottom: 16px;
      display: block;
    }

    .empty-state h3 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.2rem;
      margin-bottom: 6px;
    }

    .empty-state p {
      color: #64748b;
      font-size: 0.88rem;
      margin-bottom: 20px;
    }

    /* Modal Styles */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(15, 23, 42, 0.6);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeIn 0.3s ease;
    }

    .modal-viewport-box {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 24px;
      padding: 40px;
      width: 90%;
      max-width: 700px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    }

    .modal-viewport-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 28px;
      padding-bottom: 18px;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
    }

    .modal-viewport-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.5rem;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .modal-viewport-header h2 i {
      color: #0ea5e9;
    }

    .modal-close-btn {
      width: 40px;
      height: 40px;
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      border-radius: 12px;
      color: #f43f5e;
      cursor: pointer;
      font-size: 1.2rem;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-close-btn:hover {
      background: #f43f5e;
      color: white;
      transform: rotate(90deg);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }

    .form-group {
      margin-bottom: 4px;
    }

    .form-group.full-width {
      grid-column: 1 / -1;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
    }

    .form-group label .required {
      color: #f43f5e;
      margin-left: 4px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .form-group textarea {
      resize: vertical;
      min-height: 80px;
    }

    .file-upload-wrapper {
      position: relative;
      border: 2px dashed rgba(14, 165, 233, 0.3);
      border-radius: 12px;
      padding: 20px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.3);
    }

    .file-upload-wrapper:hover {
      border-color: #0ea5e9;
      background: rgba(255, 255, 255, 0.5);
    }

    .file-upload-wrapper input[type="file"] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    .file-upload-wrapper .upload-icon {
      font-size: 2rem;
      color: #0ea5e9;
      margin-bottom: 8px;
      display: block;
    }

    .file-upload-wrapper .upload-text {
      color: #64748b;
      font-size: 0.9rem;
      font-weight: 600;
    }

    .file-upload-wrapper .upload-text span {
      color: #0ea5e9;
      font-weight: 700;
    }

    .image-preview-wrapper {
      margin-top: 12px;
      position: relative;
      display: inline-block;
      border-radius: 12px;
      overflow: hidden;
      border: 2px solid rgba(14, 165, 233, 0.2);
    }

    .image-preview-wrapper img {
      width: 120px;
      height: 120px;
      object-fit: cover;
      display: block;
    }

    .image-preview-wrapper .remove-image {
      position: absolute;
      top: 4px;
      right: 4px;
      background: rgba(244, 63, 94, 0.9);
      color: white;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .image-preview-wrapper .remove-image:hover {
      transform: scale(1.1);
      background: #f43f5e;
    }

    .upload-progress {
      margin-top: 12px;
      background: rgba(14, 165, 233, 0.1);
      border-radius: 8px;
      height: 6px;
      overflow: hidden;
    }

    .upload-progress .progress-bar {
      height: 100%;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      transition: width 0.3s ease;
      border-radius: 8px;
    }

    .checkbox-wrapper {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      padding: 8px 0;
    }

    .checkbox-wrapper input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: #0ea5e9;
      cursor: pointer;
    }

    .checkbox-wrapper label {
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      cursor: pointer;
      margin: 0;
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 28px;
      padding-top: 20px;
      border-top: 2px solid rgba(14, 165, 233, 0.1);
    }

    .btn-cancel {
      padding: 12px 28px;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      color: #334155;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      background: #ffffff;
      border-color: #334155;
      transform: translateY(-2px);
    }

    .btn-submit {
      padding: 12px 32px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-submit .spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes fadeIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .sliders-grid {
        grid-template-columns: 1fr;
      }
      .form-grid {
        grid-template-columns: 1fr;
      }
      .sliders-header {
        flex-direction: column;
        align-items: stretch;
        text-align: center;
      }
      .sliders-header button {
        width: 100%;
        justify-content: center;
      }
      .modal-viewport-box {
        padding: 24px;
        max-height: 95vh;
      }
      .modal-viewport-header h2 {
        font-size: 1.2rem;
      }
      .modal-footer {
        flex-direction: column;
      }
      .modal-footer button {
        width: 100%;
        justify-content: center;
      }
    }
  `;

  if (loading && sliders.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="sliders-page">
        <div className="welcome-hero">
          <h1>📸 Sliders Management</h1>
          <p>Configure front homepage showcase slideshow carousels, update order sequencing tiers, and link custom buttons.</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        <div className="sliders-header">
          <h2><i className="fas fa-images"></i> Active Showcase Sliders</h2>
          <button className="btn-add-slider" onClick={openCreateModal}>
            <i className="fas fa-plus-circle"></i> Add New Slider
          </button>
        </div>

        <div className="sliders-grid">
          {sliders.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-images"></i>
              <h3>No Showcase Sliders Located</h3>
              <p>The slide carousel registration queue holds zero live metrics assets.</p>
              <button className="btn-add-slider" onClick={openCreateModal}>
                <i className="fas fa-plus-circle"></i> Add Your First Slider
              </button>
            </div>
          ) : (
            sliders.map((slider) => (
              <div className="slider-card" key={slider.id}>
                <div className="slider-image-wrapper">
                  {slider.image ? (
                    <img src={slider.image} alt={slider.title} className="slider-actual-img" />
                  ) : (
                    <i className="fas fa-image" style={{ fontSize: '44px', opacity: 0.5 }}></i>
                  )}
                  <div className="slider-image-overlay">
                    <span className="slider-order-badge">
                      Priority: {slider.order || 0}
                    </span>
                  </div>
                </div>
                <div className="slider-info">
                  <span className={`slider-badge ${slider.active ? 'badge-active' : 'badge-inactive'}`}>
                    {slider.active ? 'Active' : 'Inactive'}
                  </span>
                  <h3 className="slider-title">{slider.title || 'Untitled Showcase Slide'}</h3>
                  <p className="slider-subtitle">{slider.subtitle || 'No description assigned.'}</p>
                  {slider.buttonText && (
                    <div style={{ marginTop: '8px', marginBottom: '12px' }}>
                      <span className="btn-row-action btn-row-edit" style={{ maxWidth: '140px', fontSize: '11px', padding: '6px 12px', cursor: 'default' }}>
                        <i className="fas fa-link"></i> {slider.buttonText}
                      </span>
                    </div>
                  )}
                  <div className="slider-actions">
                    <button className="btn-row-action btn-row-edit" onClick={() => openEditModal(slider)}>
                      <i className="fas fa-pen-to-square"></i> Edit
                    </button>
                    <button className="btn-row-action btn-row-delete" onClick={() => handleDelete(slider.id, slider.image)}>
                      <i className="fas fa-trash-can"></i> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Modern Modal */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-viewport-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className={`fas ${editingSlider ? 'fa-edit' : 'fa-plus-circle'}`}></i>
                  {editingSlider ? 'Edit Slider' : 'Create New Slider'}
                </h2>
                <button className="modal-close-btn" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  {/* Title */}
                  <div className="form-group full-width">
                    <label>Slider Title <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Enter main heading text line"
                      required
                    />
                  </div>

                  {/* Subtitle */}
                  <div className="form-group full-width">
                    <label>Slider Subtitle</label>
                    <textarea
                      value={formData.subtitle}
                      onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                      rows="3"
                      placeholder="Enter supporting subtitle summary description..."
                    />
                  </div>

                  {/* Order & Button Text */}
                  <div className="form-group">
                    <label>Display Order Priority</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      placeholder="Lower numbers render first"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Button Action Text</label>
                    <input
                      type="text"
                      value={formData.buttonText}
                      onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                      placeholder="e.g., Read More"
                    />
                  </div>

                  {/* Button Link */}
                  <div className="form-group full-width">
                    <label>Button Destination Link</label>
                    <input
                      type="url"
                      value={formData.buttonLink}
                      onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                      placeholder="https://example.com/target-path"
                    />
                  </div>

                  {/* Image Upload */}
                  <div className="form-group full-width">
                    <label>Slider Background Image</label>
                    <div className="file-upload-wrapper">
                      <i className="fas fa-cloud-upload-alt upload-icon"></i>
                      <div className="upload-text">
                        Drag & drop or <span>browse</span> to upload image
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </div>
                    {imagePreview && (
                      <div className="image-preview-wrapper">
                        <img src={imagePreview} alt="Preview" />
                        <button
                          type="button"
                          className="remove-image"
                          onClick={() => {
                            setImagePreview('');
                            setImageFile(null);
                            setFormData({ ...formData, image: null });
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                    {isUploading && uploadProgress > 0 && (
                      <div className="upload-progress">
                        <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                      </div>
                    )}
                  </div>

                  {/* Active Status */}
                  <div className="form-group full-width">
                    <div className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        id="activeCheckbox"
                      />
                      <label htmlFor="activeCheckbox">
                        Active Status (Show inside homepage viewport carousel loop)
                      </label>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={isUploading}>
                    {isUploading ? (
                      <>
                        <span className="spinner"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {editingSlider ? 'Update Slider' : 'Create Slider'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Sliders;